import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { Eye, EyeOff, Check, X, Loader2, Mail, ArrowRight, KeyRound } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const [step, setStep] = useState(1); // 1: Email, 2: Verify Code, 3: New Password
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  // Password validation rules
  const passwordRules = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSymbol: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
  };

  const isPasswordValid = Object.values(passwordRules).every(Boolean);

  // Step 1: Send reset code
  const handleSendCode = async (e) => {
    e.preventDefault();
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('يرجى إدخال بريد إلكتروني صحيح');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password?email=${encodeURIComponent(email)}&platform=cloudgm`);
      toast.success('إذا كان البريد مسجلاً، سيصلك رمز التحقق');
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify code
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    
    if (verificationCode.length !== 6) {
      toast.error('يرجى إدخال رمز التحقق المكون من 6 أرقام');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/auth/verify-reset-code?email=${encodeURIComponent(email)}&code=${verificationCode}`);
      toast.success('تم التحقق بنجاح');
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'رمز التحقق غير صحيح');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Reset password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!isPasswordValid) {
      toast.error('كلمة المرور لا تستوفي الشروط المطلوبة');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('كلمة المرور غير متطابقة');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/auth/reset-password?email=${encodeURIComponent(email)}&code=${verificationCode}&new_password=${encodeURIComponent(password)}`);
      toast.success('تم تغيير كلمة المرور بنجاح');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'حدث خطأ أثناء تغيير كلمة المرور');
    } finally {
      setLoading(false);
    }
  };

  const PasswordRule = ({ passed, text }) => (
    <div className={`flex items-center gap-2 text-sm ${passed ? 'text-green-600' : 'text-[#94A3B8]'}`}>
      {passed ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
      <span>{text}</span>
    </div>
  );

  // Progress Steps
  const StepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3].map((s, idx) => (
        <div key={s} className="flex items-center">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
            step >= s ? 'bg-[#EF4444] text-white' : 'bg-[#E2E8F0] text-[#94A3B8]'
          }`}>
            {step > s ? <Check className="h-5 w-5" /> : s}
          </div>
          {idx < 2 && (
            <div className={`w-16 h-1 mx-2 transition-all ${step > s ? 'bg-[#EF4444]' : 'bg-[#E2E8F0]'}`} />
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-gradient-to-br from-[#F8FAFC] to-[#E2E8F0]">
      <div className="max-w-lg w-full">
        <div className="text-center mb-6">
          <img 
            src="https://customer-assets.emergentagent.com/job_cloudgm-hub/artifacts/pggsc8pu_freepik__background__28286.png"
            alt="CloudGM Logo"
            className="h-16 w-auto mx-auto mb-4"
          />
          <h1 className="text-3xl font-bold text-[#0F172A] font-heading mb-2" data-testid="forgot-password-title">
            استعادة كلمة المرور
          </h1>
          <p className="text-[#475569] font-body">
            {step === 1 && 'أدخل بريدك الإلكتروني لاستعادة كلمة المرور'}
            {step === 2 && 'أدخل رمز التحقق المرسل إلى بريدك'}
            {step === 3 && 'أنشئ كلمة مرور جديدة'}
          </p>
        </div>

        <StepIndicator />

        <div className="bg-white rounded-2xl shadow-xl p-8 border border-[#E2E8F0]">
          
          {/* Step 1: Email Entry */}
          {step === 1 && (
            <form onSubmit={handleSendCode} className="space-y-5" data-testid="email-form">
              <div>
                <Label htmlFor="email" className="text-[#0F172A] font-body text-sm">
                  البريد الإلكتروني
                </Label>
                <Input
                  id="email"
                  type="email"
                  required
                  placeholder="email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5"
                  data-testid="forgot-email-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#EF4444] hover:bg-[#DC2626] text-white py-6 text-lg"
                data-testid="send-reset-code-btn"
              >
                {loading ? (
                  <><Loader2 className="h-5 w-5 animate-spin me-2" /> جاري الإرسال...</>
                ) : (
                  <><Mail className="h-5 w-5 me-2" /> إرسال رمز التحقق</>
                )}
              </Button>
            </form>
          )}

          {/* Step 2: Verify Code */}
          {step === 2 && (
            <form onSubmit={handleVerifyCode} className="space-y-5" data-testid="verify-form">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-[#EF4444]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-8 w-8 text-[#EF4444]" />
                </div>
                <p className="text-[#475569] text-sm">
                  تم إرسال رمز التحقق إلى
                </p>
                <p className="text-[#0F172A] font-bold">{email}</p>
              </div>

              <div>
                <Label htmlFor="verificationCode" className="text-[#0F172A] font-body text-sm mb-3 block text-center">
                  رمز التحقق (6 أرقام)
                </Label>
                <div className="flex justify-center" dir="ltr">
                  <InputOTP
                    maxLength={6}
                    value={verificationCode}
                    onChange={(value) => setVerificationCode(value)}
                    data-testid="reset-verification-code-input"
                  >
                    <InputOTPGroup className="gap-2">
                      <InputOTPSlot 
                        index={0} 
                        className="w-12 h-14 text-2xl font-bold rounded-lg border-2 bg-white border-[#E2E8F0] text-[#0F172A] focus:border-[#EF4444] focus:ring-2 focus:ring-[#EF4444]/20"
                      />
                      <InputOTPSlot 
                        index={1} 
                        className="w-12 h-14 text-2xl font-bold rounded-lg border-2 bg-white border-[#E2E8F0] text-[#0F172A] focus:border-[#EF4444] focus:ring-2 focus:ring-[#EF4444]/20"
                      />
                      <InputOTPSlot 
                        index={2} 
                        className="w-12 h-14 text-2xl font-bold rounded-lg border-2 bg-white border-[#E2E8F0] text-[#0F172A] focus:border-[#EF4444] focus:ring-2 focus:ring-[#EF4444]/20"
                      />
                      <InputOTPSlot 
                        index={3} 
                        className="w-12 h-14 text-2xl font-bold rounded-lg border-2 bg-white border-[#E2E8F0] text-[#0F172A] focus:border-[#EF4444] focus:ring-2 focus:ring-[#EF4444]/20"
                      />
                      <InputOTPSlot 
                        index={4} 
                        className="w-12 h-14 text-2xl font-bold rounded-lg border-2 bg-white border-[#E2E8F0] text-[#0F172A] focus:border-[#EF4444] focus:ring-2 focus:ring-[#EF4444]/20"
                      />
                      <InputOTPSlot 
                        index={5} 
                        className="w-12 h-14 text-2xl font-bold rounded-lg border-2 bg-white border-[#E2E8F0] text-[#0F172A] focus:border-[#EF4444] focus:ring-2 focus:ring-[#EF4444]/20"
                      />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading || verificationCode.length !== 6}
                className="w-full bg-[#EF4444] hover:bg-[#DC2626] text-white py-6 text-lg"
                data-testid="verify-reset-code-btn"
              >
                {loading ? (
                  <><Loader2 className="h-5 w-5 animate-spin me-2" /> جاري التحقق...</>
                ) : (
                  <><Check className="h-5 w-5 me-2" /> تأكيد الرمز</>
                )}
              </Button>

              <button
                type="button"
                onClick={() => setStep(1)}
                className="w-full text-center text-[#EF4444] hover:underline text-sm"
              >
                تغيير البريد الإلكتروني
              </button>
            </form>
          )}

          {/* Step 3: New Password */}
          {step === 3 && (
            <form onSubmit={handleResetPassword} className="space-y-5" data-testid="new-password-form">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex items-center gap-2">
                <Check className="h-5 w-5 text-green-600" />
                <span className="text-green-700 text-sm">تم التحقق بنجاح - أنشئ كلمة مرور جديدة</span>
              </div>

              {/* New Password */}
              <div>
                <Label htmlFor="password" className="text-[#0F172A] font-body text-sm">
                  كلمة المرور الجديدة
                </Label>
                <div className="relative mt-1.5">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pe-10"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#475569]"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                
                {/* Password Rules */}
                <div className="mt-3 p-3 bg-[#F8FAFC] rounded-lg space-y-1">
                  <p className="text-xs text-[#475569] mb-2 font-medium">متطلبات كلمة المرور:</p>
                  <PasswordRule passed={passwordRules.minLength} text="8 أحرف على الأقل" />
                  <PasswordRule passed={passwordRules.hasUppercase} text="حرف كبير واحد على الأقل (A-Z)" />
                  <PasswordRule passed={passwordRules.hasLowercase} text="حرف صغير واحد على الأقل (a-z)" />
                  <PasswordRule passed={passwordRules.hasNumber} text="رقم واحد على الأقل (0-9)" />
                  <PasswordRule passed={passwordRules.hasSymbol} text="رمز واحد على الأقل (!@#$...)" />
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <Label htmlFor="confirmPassword" className="text-[#0F172A] font-body text-sm">
                  تأكيد كلمة المرور
                </Label>
                <div className="relative mt-1.5">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pe-10"
                    data-testid="confirm-new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#475569]"
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {confirmPassword && password === confirmPassword && (
                  <p className="text-green-600 text-xs mt-1 flex items-center gap-1">
                    <Check className="h-3 w-3" /> كلمة المرور متطابقة
                  </p>
                )}
              </div>

              <Button
                type="submit"
                disabled={loading || !isPasswordValid || password !== confirmPassword}
                className="w-full bg-[#EF4444] hover:bg-[#DC2626] text-white py-6 text-lg mt-6"
                data-testid="reset-password-btn"
              >
                {loading ? (
                  <><Loader2 className="h-5 w-5 animate-spin me-2" /> جاري التغيير...</>
                ) : (
                  <><KeyRound className="h-5 w-5 me-2" /> تغيير كلمة المرور</>
                )}
              </Button>
            </form>
          )}

          <div className="mt-6 text-center">
            <p className="text-[#475569] font-body">
              تذكرت كلمة المرور؟{' '}
              <Link to="/login" className="text-[#1E9BD9] hover:underline font-medium" data-testid="login-link">
                تسجيل الدخول
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
